import React from 'react';
import ListMenu from '@rio-cloud/rio-uikit/ListMenu';
import { ChapterMenuLink } from './ChapterMenuLink';
import {
  userManualDialogChapters,
  UserManualDialogChaptersKeys,
} from '../../../state/dialogs/chapters';
import { CustomFormattedMessage, TranslationsKeys } from '../../../i18n/i18n';
import { useAppDispatch, useAppSelector } from '../../../state/hooks';
import {
  getOpenChapter,
  openUserManualDialog,
} from '../../../state/dialogs/useUserManualDialog';

type ChapterGroup = {
  title: TranslationsKeys;
  chapters: UserManualDialogChaptersKeys[];
};

const chapterTitles: {
  [key in UserManualDialogChaptersKeys]: TranslationsKeys;
} = {
  getStartedWelcome: 'userManual.chapter.getStarted.welcome',
  getStartedGuide: 'userManual.chapter.getStarted.guide',
  getStartedOverview: 'userManual.chapter.getStarted.overview',
  dashboardDashboardFunctions:
    'userManual.chapter.dashboard.dashboardFunctions',
  bookingsBookingOverview: 'userManual.chapter.bookingOverview',
  servicesFuelling: 'userManual.chapter.services.fuelling',
  servicesMobileFuelling: 'userManual.chapter.services.mobileFuelling',
  servicesParking: 'userManual.chapter.services.parking',
  servicesWashing: 'userManual.chapter.services.washing',
  servicesRepairAndMaintenance:
    'userManual.chapter.services.repairAndMaintenance',
  servicesCharging: 'userManual.chapter.services.charging',
  vehicleOverview: 'userManual.chapter.vehicleOverview',
  addingServiceCards: 'userManual.cardManagement.addingCards.headline',
  activatingServices: 'userManual.chapter.activatingAService',
  transactionsList: 'userManual.chapter.transactionsList',
};

export const UserManualDialogMenu: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeChapter = useAppSelector(getOpenChapter);

  const chapterGroups: ChapterGroup[] = [
    {
      title: 'userManual.chapterGroup.getStarted',
      chapters: [
        userManualDialogChapters.getStartedWelcome,
        userManualDialogChapters.getStartedGuide,
        userManualDialogChapters.getStartedOverview,
      ],
    },
    {
      title: 'userManual.chapterGroup.cardManagement',
      chapters: [
        userManualDialogChapters.vehicleOverview,
        userManualDialogChapters.addingServiceCards,
        userManualDialogChapters.activatingServices,
      ],
    },
    {
      title: 'userManual.chapterGroup.dashboard',
      chapters: [userManualDialogChapters.dashboardDashboardFunctions],
    },
    {
      title: 'userManual.chapterGroup.transactionOverview',
      chapters: [userManualDialogChapters.transactionsList],
    },
    {
      title: 'userManual.chapterGroup.bookings',
      chapters: [userManualDialogChapters.bookingsBookingOverview],
    },
    {
      title: 'userManual.chapterGroup.services',
      chapters: [
        userManualDialogChapters.servicesFuelling,
        userManualDialogChapters.servicesMobileFuelling,
        userManualDialogChapters.servicesParking,
        userManualDialogChapters.servicesWashing,
        userManualDialogChapters.servicesRepairAndMaintenance,
        userManualDialogChapters.servicesCharging,
      ],
    },
  ];

  const buildChapterGroup = (chapterGroup: ChapterGroup) => {
    return {
      group: <CustomFormattedMessage id={chapterGroup.title} />,
      navItems: chapterGroup.chapters.map((chapter) => ({
        key: chapter,
        item: (
          <ChapterMenuLink
            chapter={chapter}
            isActive={activeChapter === chapter}
            onClick={() => handleChapterSelected(chapter)}
            title={chapterTitles[chapter]}
          />
        ),
      })),
    };
  };

  const handleChapterSelected = (chapter: UserManualDialogChaptersKeys) => {
    dispatch(openUserManualDialog(chapter));
  };

  return (
    <ListMenu
      data-testid={'user-manual-menu'}
      menuItems={chapterGroups.map((chapterGroup) =>
        buildChapterGroup(chapterGroup),
      )}
      groupClassName={'min-width-200 padding-left-0'}
    />
  );
};
